import { PublicFooter, PublicHeader } from "@/components/PublicMarketingLayout";

const sections = [
  {
    title: "Who we are",
    body: [
      "Alchemy Blueprint Engine is operated by Godswill Arum, trading as AI Traffic Alchemy.",
      "Business address: 30 Charles Street, Reading, RG1 7DB, United Kingdom.",
      "Support and privacy contact: support@aitrafficalchemy.com.",
      "TODO: Add ICO registration number, if applicable.",
    ],
  },
  {
    title: "Information we collect",
    body: [
      "Account information, including name, email address, authentication identifiers and account status.",
      "Blueprint content, including app ideas, guided answers, generated specifications, generated prompts, acceptance criteria, edge cases and test scenarios.",
      "Project and usage information, including saved projects, blueprint counts, plan limits, entitlement status and generation history.",
      "Purchase and billing records, including purchase channel, product or price IDs, transaction IDs, entitlement changes, refunds, webhook events and support notes.",
      "Technical information such as IP-derived request data, browser/device information, security logs, error logs and basic operational diagnostics.",
    ],
  },
  {
    title: "How we collect information",
    body: [
      "You provide information when you create an account, describe an app idea, answer guided questions, generate a blueprint, contact support or make a purchase.",
      "Some information is created automatically when you use the service, such as usage counts, timestamps, entitlement checks and technical logs.",
      "Purchase events may be received from Stripe, WarriorPlus or related purchase-channel webhooks and matched to user accounts by email.",
    ],
  },
  {
    title: "How we use information",
    body: [
      "To create and manage your account.",
      "To generate, save and display your blueprints and implementation prompts.",
      "To enforce plan limits, entitlements and access rules.",
      "To process payments, refunds, subscriptions, purchase events and billing support.",
      "To send operational, onboarding or buyer-related messages where configured through GoHighLevel.",
      "To protect the service, troubleshoot errors, prevent abuse and improve product quality.",
    ],
  },
  {
    title: "Lawful bases for processing",
    body: [
      "Contract: to provide the service you request, including account access, blueprint generation and paid-plan access.",
      "Legitimate interests: to secure the service, diagnose issues, prevent abuse, improve features and maintain accurate entitlement records.",
      "Legal obligation: to keep accounting, tax, refund and compliance records where required.",
      "Consent: where a specific optional use requires consent.",
    ],
  },
  {
    title: "Authentication and account data",
    body: [
      "Authentication is provided by Clerk. Passwords are handled by Clerk and are not stored directly by this application.",
      "The application stores the local account and entitlement information needed to connect your Clerk identity to your projects, blueprints and purchases.",
    ],
  },
  {
    title: "Payments and purchase records",
    body: [
      "Direct in-app payments and subscriptions are handled through Stripe. Payment-card details are handled by Stripe and are not stored directly by this application.",
      "WarriorPlus may be used for third-party purchase flows. WarriorPlus purchase events may be received through direct or GoHighLevel-forwarded webhooks.",
      "Purchase records are stored so we can grant access, prevent duplicate processing, process refunds, provide support and keep accounting records.",
    ],
  },
  {
    title: "AI-generated blueprint content and user submissions",
    body: [
      "Users submit app ideas and related content so the service can generate blueprints, product specifications, data models, test scenarios and implementation prompts.",
      "Blueprint generation uses OpenAI through server-side API calls. Your submitted idea content may be sent to OpenAI to generate the requested output.",
      "Do not submit sensitive personal data, confidential client data, API keys, passwords, trade secrets or content you do not have permission to use.",
      "AI outputs may contain errors, omissions or unsuitable recommendations. You should review generated content before using it to build or launch an app.",
    ],
  },
  {
    title: "Service providers and data sharing",
    body: [
      "Clerk is used for authentication and account identity.",
      "Stripe is used for direct payments, subscriptions, billing portal access and payment events.",
      "WarriorPlus may be used for third-party purchase processing.",
      "GoHighLevel may be used for buyer contact records, tags, course access and email automation connected to purchases or signup events.",
      "OpenAI is used for blueprint generation.",
      "Replit or the current hosting provider hosts the application runtime.",
      "PostgreSQL/database hosting stores application data, projects, blueprints, entitlements and purchase records.",
      "We may share information with these providers only as needed to operate, secure and support the service.",
    ],
  },
  {
    title: "Cookies and similar technologies",
    body: [
      "The codebase uses Clerk authentication, which may rely on cookies or similar technologies that are necessary for login, session management and security.",
      "No non-essential analytics, advertising pixels or behavioural tracking scripts were found in the inspected client code. A fake consent banner has not been added.",
    ],
  },
  {
    title: "Data retention",
    body: [
      "Account and saved-content data is retained while the account remains active.",
      "Deleted account data is normally deleted or anonymised within 30 days, with backup copies retained for up to 90 days.",
      "Purchase, entitlement and accounting records may be retained for up to 6 years.",
      "Support correspondence is generally retained for 2 years, security and audit logs for 12 months, and technical error logs for 90 days.",
    ],
  },
  {
    title: "Data security",
    body: [
      "We use access controls, authentication, server-side API handling and operational logging to help protect the service.",
      "No online service can be guaranteed completely secure. Users should avoid submitting sensitive or confidential information that is not needed for blueprint generation.",
    ],
  },
  {
    title: "International data transfers",
    body: [
      "Some service providers may process data outside the United Kingdom. Where this happens, we rely on appropriate contractual, technical or legal safeguards provided by those processors.",
    ],
  },
  {
    title: "User data rights",
    body: [
      "Depending on where you live, you may have rights to access, correct, delete, restrict or object to processing of your personal data, and to request portability.",
      "To make a request, contact support@aitrafficalchemy.com. We may need to verify your identity before responding.",
    ],
  },
  {
    title: "Children's privacy",
    body: [
      "The service is intended for users aged 18 or over. It is not directed to children.",
    ],
  },
  {
    title: "Changes to this policy",
    body: [
      "We may update this policy as the service changes. The updated version will be posted on this page with a revised date.",
    ],
  },
  {
    title: "How to contact us",
    body: [
      "Email: support@aitrafficalchemy.com.",
      "Address: 30 Charles Street, Reading, RG1 7DB, United Kingdom.",
    ],
  },
  {
    title: "How to complain to the relevant data protection authority",
    body: [
      "If you are in the United Kingdom, you can contact the Information Commissioner's Office at ico.org.uk.",
      "You may also have the right to complain to the relevant data protection authority in your country of residence.",
    ],
  },
];

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-white via-indigo-50/40 to-white text-slate-950">
      <PublicHeader />
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">
          Legal
        </p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-950">
          Privacy Policy
        </h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          Last updated: 4 July 2026
        </p>
        <div className="mt-8 space-y-6 rounded-[2rem] border border-indigo-100 bg-white p-5 shadow-sm sm:p-8">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-bold text-slate-950">{section.title}</h2>
              <div className="mt-3 space-y-3 text-sm leading-7 text-slate-600">
                {section.body.map((paragraph) => (
                  <p
                    key={paragraph}
                    className={paragraph.startsWith("TODO:") ? "font-semibold text-amber-700" : undefined}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
